import { z } from 'zod';
import type { Product } from './products';

/** What a clearance can be for. Each product offers its own subset (`PRODUCT_PRODUCTION_TYPES`). */
export const PRODUCTION_TYPES = [
  'feature_film',
  'tv_series',
  'video_game',
  'board_game',
  'podcast',
  'other',
] as const;
export const productionTypeSchema = z.enum(PRODUCTION_TYPES);
export type ProductionType = z.infer<typeof productionTypeSchema>;

/** The production types each product's form offers. Mirrors `title_production_type_is_valid` for `title`. */
export const PRODUCT_PRODUCTION_TYPES: Partial<Record<Product, readonly ProductionType[]>> = {
  title: PRODUCTION_TYPES,
};

/**
 * A row of `public.title_projects`: the intake details of a title clearance. The title and the production type
 * are empty while the form is being filled in; an order cannot be submitted without them.
 */
export const titleProjectSchema = z.object({
  project_id: z.uuid(),
  title: z.string().min(1).max(200).nullable(),
  production_type: productionTypeSchema.nullable(),
  production_company: z.string().min(1).max(200).nullable(),
  notes: z.string().min(1).max(4000).nullable(),
});
export type TitleProject = z.infer<typeof titleProjectSchema>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => v || null);

/** What `update_title_project` takes: every field optional while drafting, trimmed, empty meaning none. */
export const titleProjectInputSchema = z.strictObject({
  title: optionalText(200),
  productionType: productionTypeSchema.nullable(),
  productionCompany: optionalText(200),
  notes: optionalText(4000),
});
export type TitleProjectInput = z.infer<typeof titleProjectInputSchema>;
